// components/Navbar.js
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import LanguageDropdown from '../LanguageDropdown';
import { isRTL } from '../../i18n/config';
import SaasContainer from '../saas/Container';
import SaasButton from '../saas/Button';
import NavbarWordmark from '../branding/NavbarWordmark';

function dashboardHrefForRole(role) {
  switch (role) {
    case 'admin':
      return '/admin';
    case 'business':
      return '/business';
    case 'trader':
      return '/merchant';
    case 'flasher_staff':
      return '/supplier';
    default:
      return '/user';
  }
}

/** آیکن فروشنده / ویترین — جمع‌وجور برای نوبار */
function SellersGlyph({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 21h18M5 21V10.9a1 1 0 011-.9h4a1 1 0 011 .9V21M13 21V7.47a1 1 0 011-.9h4a1 1 0 011 .9V21M9 11h6M9 7h6M9 3h6"
      />
      <circle cx={8} cy={6} r={1.15} fill="currentColor" stroke="none" />
      <circle cx={16} cy={14} r={1.15} fill="currentColor" stroke="none" />
    </svg>
  );
}

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const isLTR = !isRTL(i18n.language);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  const { user, isAuthenticated, logout } = useAuth();
  const userDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const dashboardPaths = [
    '/admin',
    '/business',
    '/business-registration',
    '/business-pending',
    '/business-dashboard',
    '/merchant',
    '/merchant-pending',
    '/merchant/products',
    '/merchant/add-product',
    '/supplier',
    '/supplier/products',
    '/supplier/add-product',
    '/user',
    '/user/profile',
  ];
  const isDashboardPage = dashboardPaths.includes(location.pathname);

  /** لینک اصلی سایت؛ حالت فعال و غیرفعال برای خوانایی ۲۰۲۶ */
  const desktopNavPill =
    'type-nav-link shrink-0 whitespace-nowrap rounded-full px-3.5 py-2 transition-[color,background,box-shadow] duration-150';
  const navInactive = `${desktopNavPill} text-slate-600 hover:bg-black/[0.04] hover:text-ink-graphite`;
  const navActive = `${desktopNavPill} bg-signal/12 text-signal shadow-[inset_0_0_0_1px_rgba(113,75,103,0.22)]`;

  const isNavActive = (to, anchorPath) => {
    if (to) {
      if (to === '/') return pathname === '/';
      return pathname === to || pathname.startsWith(`${to}/`);
    }
    if (anchorPath) return pathname === anchorPath;
    return false;
  };

  const isHomePage = pathname === '/';

  if (isDashboardPage) {
    return null;
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 pt-3 md:pt-4">
      <SaasContainer className="relative">
        <div
          className={`flex flex-col overflow-visible rounded-2xl md:h-[62px] md:flex-row md:items-center md:justify-between md:gap-6 md:px-2 md:py-2 ${
            isHomePage
              ? 'border border-white/50 bg-card-surface/75 shadow-[0_8px_32px_-14px_rgba(113,75,103,0.18)] backdrop-blur-2xl'
              : 'border border-hairline/50 bg-card-surface/[0.93] shadow-[0_8px_30px_-12px_rgba(15,23,42,0.12)] backdrop-blur-xl backdrop-saturate-150'
          }`}
          style={{
            WebkitBackdropFilter: 'blur(14px) saturate(1.2)',
          }}
        >
          {/* لوگو */}
          <div className="flex h-14 items-center justify-between gap-4 overflow-visible px-3 md:h-auto md:justify-start md:px-4">
            <NavbarWordmark />

            {/* موبایل: زبان + همبرگر */}
            <div className="flex items-center gap-2 md:hidden">
              <LanguageDropdown />
              <button
                type="button"
                className="flex h-10 w-10 flex-col items-center justify-center rounded-xl border border-hairline/80 bg-card-surface shadow-sm transition-colors hover:bg-sky-wash/70"
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-expanded={mobileMenuOpen}
                aria-controls="marketing-nav-drawer"
                aria-label={t('nav.mobileMenuAria')}
              >
                <span className="mb-1 block h-0.5 w-5 rounded-full bg-ink-graphite/80" />
                <span className="mb-1 block h-0.5 w-5 rounded-full bg-ink-graphite/80" />
                <span className="block h-0.5 w-4 rounded-full bg-ink-graphite/70" />
              </button>
            </div>
          </div>

          {/* لینک‌های دسکتاپ — دیگر با position:absolute مرکز نشده؛ با RTL همزمان‌ها نمی‌لغزند */}
          <div className="hidden md:flex md:min-w-0 md:flex-1 md:items-center md:justify-center">
            <div role="navigation" aria-label={t('nav.mainAria')} className="-mx-1 flex max-w-full items-center gap-0.5 overflow-x-auto overscroll-x-contain px-1 py-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <Link to="/" className={isNavActive('/') ? navActive : navInactive}>
                {t('nav.home')}
              </Link>
              <Link to="/products" className={isNavActive('/products') ? navActive : navInactive}>
                {t('nav.services')}
              </Link>
              <Link to="/traders" className={isNavActive('/traders') ? navActive : navInactive}>
                {t('nav.traders')}
              </Link>
              <Link to="/blog" className={isNavActive('/blog') ? navActive : navInactive}>
                {t('nav.blog')}
              </Link>
              <Link to="/about" className={isNavActive(null, '/about') ? navActive : navInactive}>
                {t('nav.about')}
              </Link>
              <Link to="/contact" className={isNavActive(null, '/contact') ? navActive : navInactive}>
                {t('nav.contact')}
              </Link>
            </div>
          </div>

          {/* اکشن‌ها — دسکتاپ */}
          <div className="hidden shrink-0 items-center gap-2 pr-4 md:flex">
            <div className="hidden md:block">
              <LanguageDropdown />
            </div>
            {!isAuthenticated && (
              <Link
                to="/join"
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border border-signal/20 bg-gradient-to-bl from-signal/[0.08] to-transparent px-3 py-2 text-[12px] font-semibold text-signal shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] transition-[transform,background-color,border-color] hover:border-signal/35 hover:from-signal/[0.12]`}
              >
                <SellersGlyph className="h-4 w-4 opacity-95" aria-hidden />
                <span>{t('nav.sellersNavCta')}</span>
              </Link>
            )}

            {!isAuthenticated ? (
              <div className="flex items-center gap-1.5 lg:gap-2">
                <SaasButton to="/register" variant="outline" className="!rounded-xl !border-hairline/90 !bg-card-surface/90 !py-2.5 !text-[13px] !font-semibold !shadow-none hover:!border-signal/35">
                  {t('nav.register')}
                </SaasButton>
                <SaasButton to="/login" variant="gradient" className="!rounded-xl !px-5 !py-2.5 !text-[13px] !font-semibold shadow-[0_10px_24px_-8px_rgba(113,75,103,0.45)]">
                  {t('nav.login')}
                </SaasButton>
              </div>
            ) : (
              <div className="relative" ref={userDropdownRef}>
                <button
                  type="button"
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className={`flex items-center gap-2.5 rounded-xl border border-hairline/80 bg-card-surface px-2.5 py-1.5 shadow-sm transition-[border-color,box-shadow] hover:border-steel/40 ${isLTR ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-full bg-sky-wash ring-2 ring-card-surface">
                    {user?.profileImage ? (
                      <img
                        src={user.profileImage}
                        alt=""
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.target.src = '/User.png';
                        }}
                      />
                    ) : (
                      <img src="/User.png" alt="" className="h-full w-full object-cover" />
                    )}
                  </div>
                  <span className={`max-w-[8.5rem] truncate text-[13px] font-semibold text-ink-graphite ${isLTR ? 'text-left' : 'text-right'}`}>
                    {user?.companyName ||
                      `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
                      t('nav.user')}
                  </span>
                  <svg className={`h-4 w-4 flex-shrink-0 text-steel ${isLTR ? 'ml-0.5' : 'mr-0.5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {userDropdownOpen && (
                  <div
                    className={`absolute top-full z-50 mt-2 min-w-[12rem] overflow-hidden rounded-xl border border-hairline/80 bg-card-surface py-1 shadow-xl ring-1 ring-black/[0.04] ${isLTR ? 'left-0' : 'right-0'}`}
                  >
                    <Link
                      to={dashboardHrefForRole(user?.role)}
                      className={`flex px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-sky-wash ${isLTR ? 'justify-start text-left' : 'justify-end text-right'}`}
                      onClick={() => setUserDropdownOpen(false)}
                    >
                      {t('nav.dashboard')}
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        logout();
                        setUserDropdownOpen(false);
                      }}
                      className={`flex w-full px-4 py-2.5 text-sm font-medium text-coral-alert hover:bg-sky-wash ${isLTR ? 'justify-start text-left' : 'justify-end text-right'}`}
                    >
                      {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* موبایل: دراپ‌داون کشویی بدون ادغام بصری لینک تماس */}
          <div id="marketing-nav-drawer" className={mobileMenuOpen ? 'border-t border-hairline/50 md:hidden' : 'hidden'}>
            <div className="animate-in max-h-[min(70vh,520px)] space-y-1 overflow-y-auto px-3 pb-4 pt-3">
              {[
                { to: '/', label: t('nav.home'), match: pathname === '/' },
                { to: '/products', label: t('nav.services'), match: pathname.startsWith('/products') },
                { to: '/traders', label: t('nav.traders'), match: pathname.startsWith('/traders') },
                { to: '/blog', label: t('nav.blog'), match: pathname.startsWith('/blog') },
                { to: '/about', label: t('nav.about'), match: pathname === '/about' },
                { to: '/contact', label: t('nav.contact'), match: pathname === '/contact' },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`block rounded-xl px-4 py-3 text-[15px] font-medium transition-colors ${
                    item.match ? 'bg-signal/10 text-signal' : 'text-slate-700 hover:bg-black/[0.04]'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}

              {!isAuthenticated && (
                <Link
                  to="/join"
                  className={`mt-2 flex items-center justify-center gap-2 rounded-xl border border-signal/25 bg-gradient-to-br from-signal/[0.08] via-card-surface to-card-surface px-4 py-3 text-[13px] font-semibold text-signal shadow-sm`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <SellersGlyph className="h-4 w-4" aria-hidden />
                  {t('nav.sellersNavCta')}
                </Link>
              )}

              {!isAuthenticated ? (
                <div className="mt-4 grid gap-2">
                  <SaasButton to="/register" variant="outline" className="w-full !rounded-xl justify-center py-3" onClick={() => setMobileMenuOpen(false)}>
                    {t('nav.register')}
                  </SaasButton>
                  <SaasButton to="/login" variant="gradient" className="w-full !rounded-xl justify-center py-3 shadow-lg" onClick={() => setMobileMenuOpen(false)}>
                    {t('nav.login')}
                  </SaasButton>
                </div>
              ) : (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-3 rounded-xl border border-hairline/80 bg-card-surface px-4 py-3 shadow-sm">
                    <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full bg-sky-wash">
                      <img src={user?.profileImage || '/User.png'} alt="" className="h-full w-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`truncate text-sm font-semibold ${isLTR ? 'text-left' : 'text-right'}`}>
                        {user?.companyName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || t('nav.user')}
                      </div>
                      <div className={`text-caption text-fog ${isLTR ? 'text-left' : 'text-right'}`}>
                        {user?.role === 'admin'
                          ? t('nav.admin')
                          : user?.role === 'business'
                            ? t('nav.business')
                            : user?.role === 'trader'
                              ? t('nav.trader')
                              : user?.role === 'flasher_staff'
                                ? t('nav.supplier')
                                : t('nav.user')}
                      </div>
                    </div>
                  </div>
                  <SaasButton to={dashboardHrefForRole(user?.role)} variant="outline" className="w-full !rounded-xl" onClick={() => setMobileMenuOpen(false)}>
                    {t('nav.dashboard')}
                  </SaasButton>
                  <button
                    type="button"
                    className="w-full rounded-xl border border-coral-alert/35 py-3 text-sm font-semibold text-coral-alert hover:bg-coral-alert/[0.06]"
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                  >
                    {t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </SaasContainer>
    </nav>
  );
};

export default Navbar;
